import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import styles from "./ColaboraButton.module.css";

const buttonVariants = {
  primary: "bg-secondary-PCS001 border border-secondary-PCS001 text-white",
  secondary: "bg-white text-secondary-PCS001 border border-gray-300",
};

const spinnerClasses = {
  primary: "border-white border-t-transparent",
  secondary: "border-secondary-PCS001 border-t-transparent",
};

/**
 * A button component with loading spinner, success ripple and error ripple animation.
 *
 * @param {object} props
 * @param {string} props.label - Button text.
 * @param {string} [props.successLabel] - Text shown during the success animation. Falls back to `label`.
 * @param {string} [props.errorLabel] - Text shown during the error animation. Falls back to `label`.
 * @param {function} [props.onClick] - Click handler.
 * @param {string} [props.className] - Additional class names for the button.
 * @param {'primary'|'secondary'} [props.variant='primary'] - Visual style variant.
 * @param {boolean} [props.isLoading=false] - Shows a spinner and disables the button.
 * @param {boolean} [props.isSuccess=false] - When `isLoading` transitions to `false` and this is `true`, triggers the success ripple animation.
 * @param {boolean} [props.isError=false] - When `isLoading` transitions to `false` and this is `true`, triggers the error ripple animation.
 * @param {function} [props.onSuccessAnimationEnd] - Called after the success animation completes.
 * @param {function} [props.onErrorAnimationEnd] - Called after the error animation completes.
 * @param {boolean} [props.disabled=false] - Disables the button.
 */
const ColaboraButton = ({
  label,
  successLabel,
  errorLabel,
  onClick,
  className,
  variant = "primary",
  isLoading = false,
  isSuccess = false,
  isError = false,
  onSuccessAnimationEnd,
  onErrorAnimationEnd,
  disabled = false,
}) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const prevIsLoading = useRef(isLoading);

  useEffect(() => {
    const wasLoading = prevIsLoading.current;
    prevIsLoading.current = isLoading;

    if (wasLoading && !isLoading) {
      if (isSuccess) {
        setShowSuccess(true);
        const timer = setTimeout(() => {
          setShowSuccess(false);
          onSuccessAnimationEnd?.();
        }, 1500);
        return () => clearTimeout(timer);
      }

      if (isError) {
        setShowError(true);
        const timer = setTimeout(() => {
          setShowError(false);
          onErrorAnimationEnd?.();
        }, 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [isLoading, isSuccess, isError, onSuccessAnimationEnd, onErrorAnimationEnd]);

  let liveText = "";
  if (isLoading) liveText = "Cargando...";
  else if (showSuccess) liveText = successLabel ?? label;
  else if (showError) liveText = errorLabel ?? label;

  const isDisabled = isLoading || disabled;
  const isUpdating = isLoading || showSuccess || showError;

  return (
    <button
      className={`relative overflow-hidden flex items-center justify-center w-full p-[.6rem] font-bold rounded-xl ${className ?? ""} ${buttonVariants[variant]} opacity-${isDisabled ? "50" : "100"} cursor-${isDisabled || isUpdating ? "not-allowed" : "pointer"}`.trim()}
      type="button"
      onClick={onClick}
      disabled={isDisabled || isUpdating}
      aria-busy={isLoading}
    >
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {liveText}
      </span>

      {showSuccess && (
        <span
          aria-hidden="true"
          className={`${styles.ripple} absolute inset-0 rounded-xl bg-success-ShadowS003`}
        />
      )}

      {showError && (
        <span
          aria-hidden="true"
          className={`${styles.rippleError} absolute inset-0 rounded-xl bg-red-500`}
        />
      )}

      {isLoading && (
        <div
          aria-hidden="true"
          className={`relative z-10 w-[20px] h-[20px] mr-1 rounded-full border-2 animate-spin ${spinnerClasses[variant]}`}
        />
      )}

      <span className={`relative z-10 ${showSuccess || showError ? "text-white" : ""}`}>
        {showSuccess ? (successLabel ?? label) : showError ? (errorLabel ?? label) : label}
      </span>
    </button>
  );
};

ColaboraButton.propTypes = {
  label: PropTypes.string.isRequired,
  successLabel: PropTypes.string,
  errorLabel: PropTypes.string,
  onClick: PropTypes.func,
  variant: PropTypes.oneOf(["primary", "secondary"]),
  className: PropTypes.string,
  isLoading: PropTypes.bool,
  isSuccess: PropTypes.bool,
  isError: PropTypes.bool,
  onSuccessAnimationEnd: PropTypes.func,
  onErrorAnimationEnd: PropTypes.func,
  disabled: PropTypes.bool,
};

export default ColaboraButton;